import { useState, useCallback } from 'react'
import { verifyDisplayPin } from '../../lib/displayApi'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

export function PinPad({ member, deviceToken, onSuccess, onBack }) {
  const [digits,    setDigits]    = useState('')
  const [error,     setError]     = useState('')
  const [attempts,  setAttempts]  = useState(0)
  const [lockedOut, setLockedOut] = useState(false)
  const [lockSecs,  setLockSecs]  = useState(0)
  const [checking,  setChecking]  = useState(false)

  const memberColor = MEMBER_COLORS[member.memberColor] ?? '#8b5cf6'

  const startLockout = useCallback(() => {
    setLockedOut(true)
    let secs = 60
    setLockSecs(secs)
    const timer = setInterval(() => {
      secs -= 1
      setLockSecs(secs)
      if (secs <= 0) {
        clearInterval(timer)
        setLockedOut(false)
        setAttempts(0)
        setDigits('')
        setError('')
      }
    }, 1000)
  }, [])

  const handleDigit = useCallback(async (d) => {
    if (lockedOut || checking) return
    const next = digits + d
    setDigits(next)
    setError('')

    if (next.length < 4) return

    setChecking(true)
    try {
      const { valid } = await verifyDisplayPin(deviceToken, member.id, next)
      if (valid) {
        onSuccess()
      } else {
        const newAttempts = attempts + 1
        setAttempts(newAttempts)
        setDigits('')
        if (newAttempts >= 3) {
          startLockout()
          setError('Too many attempts. Locked for 60s.')
        } else {
          setError(`Wrong PIN — ${3 - newAttempts} left`)
        }
      }
    } catch (_) {
      setDigits('')
      setError('Connection error. Try again.')
    } finally {
      setChecking(false)
    }
  }, [digits, lockedOut, checking, attempts, deviceToken, member.id, onSuccess, startLockout])

  const handleBack = useCallback(() => {
    setDigits((d) => d.slice(0, -1))
    setError('')
  }, [])

  const KEYS = ['1','2','3','4','5','6','7','8','9','','0','⌫']

  return (
    <div className="fixed inset-0 bg-gray-950/95 backdrop-blur-sm flex flex-col items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="flex flex-col items-center w-full max-w-xs">
        {/* Member avatar */}
        <div
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-white text-2xl sm:text-3xl font-bold mb-3 ring-4 ring-white/20 flex-shrink-0"
          style={{ backgroundColor: memberColor }}
        >
          {member.displaySettings?.avatar_emoji ?? member.displayName?.[0]?.toUpperCase()}
        </div>
        <h2 className="text-white text-xl sm:text-2xl font-bold mb-0.5">{member.displayName}</h2>
        <p className="text-gray-400 text-sm sm:text-base mb-5">Enter your PIN</p>

        {/* Dots */}
        <div className="flex gap-4 mb-4">
          {[0,1,2,3].map((i) => (
            <div
              key={i}
              className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-150 ${i < digits.length ? 'bg-white scale-110' : 'bg-gray-700'}`}
            />
          ))}
        </div>

        {/* Error / lockout */}
        <div className="h-8 flex items-center justify-center mb-2">
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          {lockedOut && !error && <p className="text-amber-400 text-sm">Locked — {lockSecs}s</p>}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-2 w-full">
          {KEYS.map((k, i) => (
            <button
              key={i}
              onClick={() => {
                if (k === '⌫') handleBack()
                else if (k) handleDigit(k)
              }}
              disabled={lockedOut || checking || !k}
              className={`h-12 sm:h-14 rounded-xl text-xl sm:text-2xl font-semibold transition-all active:scale-95 ${
                !k ? 'invisible' :
                k === '⌫' ? 'bg-white/10 text-gray-300 hover:bg-white/20' :
                'bg-white/10 text-white hover:bg-white/20 active:bg-white/30'
              } ${lockedOut || checking ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {k}
            </button>
          ))}
        </div>

        {lockedOut && (
          <p className="text-gray-500 text-xs mt-4 text-center">
            Ask a household admin to reset your PIN in DoorChart settings.
          </p>
        )}

        <button
          onClick={onBack}
          className="mt-5 text-gray-500 text-sm hover:text-gray-300 transition-colors"
        >
          ← Back
        </button>
      </div>
    </div>
  )
}
